import api from '@/services/api';
import type { AssignableDocumentType, PaginationMeta, TemplateOwner } from '../types/documentTemplates.types';
import type {
  AssignTemplateInput,
  ChangeAssignedTemplateInput,
  DocumentCoverage,
  TemplateAssignment,
  TemplateAssignmentListParams,
  TemplateAssignmentListResult,
} from '../types/templateAssignments.types';

const assignmentsPath = '/documents/template-assignments';
const collectionPath = `${assignmentsPath}/`;

interface AssignmentDto {
  id: string;
  product_id: string;
  dealer_id: string | null;
  level: 'product' | 'dealer';
  document_type: AssignableDocumentType;
  template: {
    id: string;
    name: string;
    key: string;
    owner: TemplateOwner;
    dealer_id: string | null;
    published_version_number: number | null;
  };
  updated_at: string | null;
}

interface MetaDto {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

const mapMeta = ({ current_page, last_page, per_page, total }: MetaDto): PaginationMeta => ({
  currentPage: current_page,
  lastPage: last_page,
  perPage: per_page,
  total,
  from: total === 0 ? 0 : (current_page - 1) * per_page + 1,
  to: Math.min(current_page * per_page, total),
});

const mapAssignment = (dto: AssignmentDto): TemplateAssignment => ({
  id: dto.id,
  productId: dto.product_id,
  dealerId: dto.dealer_id,
  level: dto.level,
  documentType: dto.document_type,
  template: {
    id: dto.template.id,
    name: dto.template.name,
    key: dto.template.key,
    owner: dto.template.owner,
    ownerDealerId: dto.template.dealer_id,
    publishedVersionNumber: dto.template.published_version_number,
  },
  updatedAt: dto.updated_at,
});

export const fetchTemplateAssignments = async (
  params: TemplateAssignmentListParams,
): Promise<TemplateAssignmentListResult> => {
  const response = await api.get<{ data: AssignmentDto[]; meta: MetaDto }>(collectionPath, {
    params: { product_id: params.productId, page: params.page, per_page: params.perPage },
  });
  return { assignments: response.data.data.map(mapAssignment), meta: mapMeta(response.data.meta) };
};

export const assignTemplate = async (input: AssignTemplateInput): Promise<TemplateAssignment> => {
  const response = await api.post<{ data: AssignmentDto }>(collectionPath, {
    product_id: input.productId,
    dealer_id: input.dealerId,
    document_type: input.documentType,
    document_template_id: input.templateId,
  });
  return mapAssignment(response.data.data);
};

export const changeAssignedTemplate = async (input: ChangeAssignedTemplateInput): Promise<TemplateAssignment> => {
  const response = await api.put<{ data: AssignmentDto }>(`${assignmentsPath}/${input.assignmentId}`, {
    document_template_id: input.templateId,
  });
  return mapAssignment(response.data.data);
};

export const unassignTemplate = async (assignmentId: string): Promise<void> => {
  await api.delete(`${assignmentsPath}/${assignmentId}`);
};

export const fetchDocumentCoverage = async (productIds: string[]): Promise<DocumentCoverage[]> => {
  const response = await api.get<{ data: { product_id: string; missing: AssignableDocumentType[] }[] }>(
    `${assignmentsPath}/coverage`,
    { params: { product_ids: productIds } },
  );
  return response.data.data.map((entry) => ({ productId: entry.product_id, missing: entry.missing }));
};
